import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from '@/components/ui/table';
import { AlertCircle, RefreshCw, Download, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

// ---------------------------------------------------------------------------
// Cost & revenue-per-customer dashboard (super-admin). Consumes the P4 API
// GET /api/billing-usage/cost-dashboard?month=YYYY-MM. Matches the KG Readiness
// Map precedent (per-site sortable table + row drill-down).
// ---------------------------------------------------------------------------

interface Coverage {
  apgCount: number;
  billedApgCount: number;
  unbilledApgCount: number; // >0 = APG rows with no matching billing event
  duplicateBillingCount: number; // >0 = billing events that duplicate a billed APG
}

interface SiteRow {
  siteId: string;
  siteName: string | null;
  siteUrl: string | null;
  aeoGenerations: number;
  regenerations: number;
  translations: number;
  faqGenerations: number;
  eventCount: number;
  inputTokens: number;
  outputTokens: number;
  llmCostUsd: number;
  aeoCostUsd: number; // AEO-only cost (new+regen) — numerator for cost-per-page
  billableEur: number;
  coverage: Coverage;
}

interface Totals {
  inputTokens: number;
  outputTokens: number;
  llmCostUsd: number;
  billableEur: number;
  eventCount: number;
}

interface Freshness {
  // When the nightly cost MV was last refreshed (null = never refresh-logged / unmigrated).
  refreshedAt: string | null;
  // True when refreshedAt is null or older than staleThresholdHours — the site/totals numbers
  // may be out of date (e.g. a stalled analytics scheduler).
  isStale: boolean;
  staleThresholdHours: number;
}

interface DashboardResponse {
  success: boolean;
  month: string;
  sites: SiteRow[];
  totals: Totals;
  freshness?: Freshness;
}

const API = '/api/billing-usage/cost-dashboard';

type SortKey =
  | 'siteName' | 'aeoGenerations' | 'regenerations' | 'translations'
  | 'inputTokens' | 'outputTokens' | 'llmCostUsd' | 'billableEur';

// ⛔ THE TOKEN COLUMNS AND THE COST COLUMN MEASURE DIFFERENT SCOPES ON THE SAME ROW,
// so they are NOT commensurable and cost/tokens is not a valid rate (task-loclbjer4d).
// On the batch AEO path billing_usage_events.input_tokens/output_tokens carry the BATCH
// SUBMISSION ONLY, while llm_cost_usd is RETRY-INCLUSIVE — the realtime
// faq_sufficiency_retry leg's tokens live only in cost_breakdown and never reach these
// columns. Measured on the 2026-08-19 cohort: 41,625,350 input tokens (~7.5% of input)
// are inside the cost column and absent from the token columns, so an implied per-token
// rate read off these two columns is overstated by ~8%.
//
// ⚠️ THE WRITER IS NOT THE DEFECT and must not be "fixed": SubBatchProcessorService
// keeps the token counts batch-only DELIBERATELY so batch-rate and realtime-rate tokens
// are never blended into a fictional per-token rate, and gives the retry its own
// cost_breakdown bucket for the same reason. The trap is here, at the presentation
// layer, where the two land as adjacent sortable columns with nothing saying they are
// measured differently.
//
// ⇒ The scope is stated in the header label AND in the CSV header, because the CSV
// leaves the product entirely and carries no tooltip. A second instance of the same
// shape was found 2026-09-01 on the azure_openai path, where a row's cost came from a
// priced retry leg while its tokens came from an unpriced generation leg — so this is a
// property of the row shape, not of one pipeline.
const COLUMNS: { key: SortKey; label: string; numeric: boolean; note?: string }[] = [
  { key: 'siteName', label: 'Site', numeric: false },
  { key: 'aeoGenerations', label: 'AEO gens', numeric: true },
  { key: 'regenerations', label: 'Regenerations', numeric: true },
  { key: 'translations', label: 'Translations', numeric: true },
  {
    key: 'inputTokens',
    label: 'Input tokens (recorded)',
    numeric: true,
    note: 'Recorded tokens, summed across generation, regeneration, translation and FAQ events. '
      + 'For SOME rows the cost includes legs whose tokens are absent from THIS column — the batch AEO '
      + 'writer records the realtime retry tokens in cost_breakdown but deliberately keeps them out of '
      + 'the aggregate counts — so cost ÷ tokens is not a valid rate: the numerator '
      + 'covers legs the denominator omits. How much varies by retry volume, model and month; on the one '
      + 'cohort measured (2026-08-19) the omitted input was ~7.5% (task-loclbjer4d).',
  },
  {
    key: 'outputTokens',
    label: 'Output tokens (recorded)',
    numeric: true,
    note: 'Recorded tokens, summed across generation, regeneration, translation and FAQ events. '
      + 'For SOME rows the cost includes legs whose tokens are absent from THIS column — the batch AEO '
      + 'writer records the realtime retry tokens in cost_breakdown but deliberately keeps them out of '
      + 'the aggregate counts — so cost ÷ tokens is not a valid rate: the numerator '
      + 'covers legs the denominator omits. How much varies by retry volume, model and month; on the one '
      + 'cohort measured (2026-08-19) the omitted input was ~7.5% (task-loclbjer4d).',
  },
  {
    key: 'llmCostUsd',
    label: 'LLM cost (USD)',
    numeric: true,
    note: 'Recorded cost, summed across all event types. NOT commensurable with the token columns: for some rows it includes legs whose tokens are absent from them (task-loclbjer4d).',
  },
  { key: 'billableEur', label: 'Billed (EUR)', numeric: true },
];

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const fmtInt = (n: number) => n.toLocaleString('en-US');
const fmtUsd = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const fmtEur = (n: number) => `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// CSV cell encoder. Neutralizes CSV formula injection: siteName/siteUrl are
// customer-controlled, so a value starting with = + - @ (or a leading tab/CR) could
// execute as a formula when the CSV is opened in Excel/Sheets. Prefix those with a
// single quote, then quote-escape (codex MINOR #3938).
function csvCell(value: unknown): string {
  let s = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

async function fetchDashboard(month: string, signal?: AbortSignal): Promise<DashboardResponse> {
  const res = await authFetch(`${API}?month=${encodeURIComponent(month)}`, { signal });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body as DashboardResponse;
}

// ---------------------------------------------------------------------------
// Sparkline — tiny inline SVG of the last 6 months of LLM cost for a site.
// ---------------------------------------------------------------------------
function Sparkline({ values }: { values: number[] }) {
  if (!values.length) return <span className="text-xs text-muted-foreground">—</span>;
  const max = Math.max(...values, 0.0001);
  const w = 120;
  const h = 28;
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible" role="img" aria-label="6-month cost trend">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Per-site drill-down row (expanded): event_type breakdown, cost/APG, sparkline.
// ---------------------------------------------------------------------------
function DrillDown({ site }: { site: SiteRow }) {
  const [trend, setTrend] = useState<number[] | null>(null);
  const [trendErr, setTrendErr] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        // One bounded, site-scoped request (not N full-dashboard scans) for the last 6
        // months of LLM cost; aborted if the row is collapsed while in flight.
        const res = await authFetch(
          `/api/billing-usage/cost-trend?siteId=${encodeURIComponent(site.siteId)}&months=6`,
          { signal: controller.signal },
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setTrend((body.points as Array<{ llmCostUsd: number }>).map((p) => p.llmCostUsd));
      } catch (e) {
        if (!controller.signal.aborted && (e as Error).name !== 'AbortError') {
          setTrendErr(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => controller.abort();
  }, [site.siteId]);

  const aeoEvents = site.aeoGenerations + site.regenerations;
  // AEO-only cost numerator (new+regen), so translations/FAQ don't inflate cost-per-page.
  const costPerApg = aeoEvents > 0 ? site.aeoCostUsd / aeoEvents : 0;

  return (
    <TableRow className="bg-muted/30">
      <TableCell colSpan={COLUMNS.length + 1}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-2">
          {/* event_type breakdown */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-2">Event type breakdown</div>
            <ul className="text-sm space-y-1">
              <li className="flex justify-between"><span>New generations</span><span>{fmtInt(site.aeoGenerations)}</span></li>
              <li className="flex justify-between"><span>Regenerations</span><span>{fmtInt(site.regenerations)}</span></li>
              <li className="flex justify-between"><span>FAQ generations</span><span>{fmtInt(site.faqGenerations)}</span></li>
              <li className="flex justify-between"><span>Translations</span><span>{fmtInt(site.translations)}</span></li>
              <li className="flex justify-between border-t pt-1 font-medium"><span>Total events</span><span>{fmtInt(site.eventCount)}</span></li>
            </ul>
          </div>

          {/* cost per APG + coverage */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-2">Cost & coverage</div>
            <ul className="text-sm space-y-1">
              <li className="flex justify-between"><span>Cost / generated page</span><span>{fmtUsd(costPerApg)}</span></li>
              <li className="flex justify-between"><span>APGs this month</span><span>{fmtInt(site.coverage.apgCount)}</span></li>
              <li className="flex justify-between"><span>Billed APGs</span><span>{fmtInt(site.coverage.billedApgCount)}</span></li>
              <li className={`flex justify-between font-medium ${site.coverage.unbilledApgCount > 0 ? 'text-amber-600' : ''}`}>
                <span>Unbilled APGs</span><span>{fmtInt(site.coverage.unbilledApgCount)}</span>
              </li>
              {site.coverage.duplicateBillingCount > 0 && (
                <li className="flex justify-between text-xs text-muted-foreground">
                  <span>Duplicate billing events</span><span>{fmtInt(site.coverage.duplicateBillingCount)}</span>
                </li>
              )}
            </ul>
          </div>

          {/* 6-month cost trend */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-2">LLM cost — last 6 months</div>
            {trendErr ? (
              <span className="text-xs text-red-500">{trendErr}</span>
            ) : trend ? (
              <div className="text-primary"><Sparkline values={trend} /></div>
            ) : (
              <Skeleton className="h-7 w-32" />
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function CostDashboard() {
  const [month, setMonth] = useState<string>(currentMonth());
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('llmCostUsd');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<string | null>(null);
  const inflightRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;
    setLoading(true);
    setError(null);
    setData(null);
    setExpanded(null);
    try {
      const result = await fetchDashboard(month, controller.signal);
      if (!controller.signal.aborted) setData(result);
    } catch (e) {
      if (!controller.signal.aborted && (e as Error).name !== 'AbortError') {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'siteName' ? 'asc' : 'desc');
    }
  };

  const sortedSites = useMemo(() => {
    const sites = data?.sites ? [...data.sites] : [];
    sites.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (sortKey === 'siteName') {
        av = (a.siteName || a.siteUrl || a.siteId).toLowerCase();
        bv = (b.siteName || b.siteUrl || b.siteId).toLowerCase();
      } else {
        av = a[sortKey];
        bv = b[sortKey];
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sites;
  }, [data, sortKey, sortDir]);

  const exportCsv = useCallback(() => {
    if (!data) return;
    // The CSV leaves the product, so the scope has to travel IN THE HEADER TEXT — a
    // tooltip cannot follow a spreadsheet, and a reader in Excel dividing the cost
    // column by the token columns is exactly the trap this row exists to close
    // (task-loclbjer4d). Verbose on purpose: this is the only place the warning can live.
    const header = ['Site', 'Site URL', 'AEO gens', 'Regenerations', 'Translations', 'FAQ gens',
      'Input tokens recorded (all event types - some rows omit realtime retry tokens)',
      'Output tokens recorded (all event types - some rows omit realtime retry tokens)',
      'LLM cost USD (NOT commensurable with the token columns - for some rows it includes legs whose tokens are absent)',
      'Billed EUR', 'Unbilled APGs', 'Duplicate billing events'];
    const rows = sortedSites.map((s) => [
      s.siteName || '', s.siteUrl || '', s.aeoGenerations, s.regenerations, s.translations,
      s.faqGenerations, s.inputTokens, s.outputTokens, s.llmCostUsd.toFixed(4),
      s.billableEur.toFixed(2), s.coverage.unbilledApgCount, s.coverage.duplicateBillingCount,
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map(csvCell).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cost-dashboard-${data.month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, sortedSites]);

  const anyGap = (data?.sites ?? []).some((s) => s.coverage.unbilledApgCount > 0);

  // MV freshness: the site/totals numbers come from the nightly-refreshed cost MV. Surface WHEN
  // it last refreshed and warn if stale (e.g. a stalled analytics scheduler) so the dashboard
  // can't silently serve frozen data.
  const freshness = data?.freshness;
  const refreshedLabel = freshness?.refreshedAt
    ? new Date(freshness.refreshedAt).toLocaleString()
    : null;

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Cost Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Per-site LLM cost &amp; revenue for the month — AEO generations, regenerations, translations,
            tokens, cost (USD) and billed (EUR). Click a row for the event-type breakdown and 6-month trend.
          </p>
          {data && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="mv-freshness">
              {refreshedLabel ? `Data as of ${refreshedLabel}` : 'Data freshness unknown (never refreshed)'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="month"
            value={month}
            max={currentMonth()}
            onChange={(e) => setMonth(e.target.value || currentMonth())}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            aria-label="Month"
          />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data || !data.sites.length} className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {freshness?.isStale && (
        // Warning tone (default Alert) to match the coverage-gap banner amber convention
        // (per m01-offering-dev, #3938 author) — stale data is a heads-up, not an error.
        <Alert data-testid="mv-stale-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription>
            Cost data may be up to {freshness.staleThresholdHours}+ hours old
            {refreshedLabel ? ` (last refreshed ${refreshedLabel})` : ' — the nightly refresh has not run'}.
            Figures below may be out of date until the next refresh.
          </AlertDescription>
        </Alert>
      )}

      {anyGap && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            One or more sites have a coverage gap — generated pages without a corresponding billing event.
            Expand a flagged row to inspect.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/*
        codex #5588 round 3 BLOCKING: the scope mismatch previously lived ONLY in the
        headers' native `title` attribute, which is hover-dependent — unavailable on
        touch, unreliable for keyboard and assistive tech, and invisible to anyone
        merely scanning the table before dividing one column by the other. The label
        suffix "(recorded)" alone does not say the columns are non-commensurable.
        This states it visibly and unconditionally; the per-column titles remain as
        supplementary detail, not as the only carrier of the warning.
      */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Cost ÷ tokens is not a valid rate.</strong> The token columns count only
          the tokens aggregated into them, while the cost column includes legs whose tokens are
          absent from those columns — the batch AEO writer records the realtime retry tokens in
          cost_breakdown but deliberately keeps them out of the aggregate counts — so the
          numerator covers legs the denominator omits. How much varies by retry volume, model
          and month; on the one cohort measured (2026-08-19) the omitted input was ~7.5%
          (task-loclbjer4d).
        </AlertDescription>
      </Alert>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    onClick={() => toggleSort(col.key)}
                    title={col.note}
                    className={`cursor-pointer select-none ${col.numeric ? 'text-right' : ''}`}
                  >
                    {col.label}
                    {sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMNS.length + 1}><Skeleton className="h-6 w-full" /></TableCell>
                  </TableRow>
                ))
              ) : sortedSites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 1} className="text-center text-muted-foreground py-8">
                    No billing activity for {data?.month ?? month}.
                  </TableCell>
                </TableRow>
              ) : (
                sortedSites.map((s) => (
                  <React.Fragment key={s.siteId}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() => setExpanded((cur) => (cur === s.siteId ? null : s.siteId))}
                    >
                      <TableCell>{expanded === s.siteId ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{s.siteName || s.siteUrl || s.siteId}</span>
                          {s.coverage.unbilledApgCount > 0 && (
                            <Badge variant="outline" className="text-amber-600 border-amber-600 gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {fmtInt(s.coverage.unbilledApgCount)} unbilled
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{fmtInt(s.aeoGenerations)}</TableCell>
                      <TableCell className="text-right">{fmtInt(s.regenerations)}</TableCell>
                      <TableCell className="text-right">{fmtInt(s.translations)}</TableCell>
                      <TableCell className="text-right">{fmtInt(s.inputTokens)}</TableCell>
                      <TableCell className="text-right">{fmtInt(s.outputTokens)}</TableCell>
                      <TableCell className="text-right">{fmtUsd(s.llmCostUsd)}</TableCell>
                      <TableCell className="text-right">{fmtEur(s.billableEur)}</TableCell>
                    </TableRow>
                    {expanded === s.siteId && <DrillDown site={s} />}
                  </React.Fragment>
                ))
              )}
            </TableBody>
            {data && sortedSites.length > 0 && (
              <TableFooter>
                <TableRow className="font-semibold border-t-2">
                  <TableCell />
                  <TableCell>Totals ({sortedSites.length} sites)</TableCell>
                  <TableCell colSpan={3} />
                  <TableCell className="text-right">{fmtInt(data.totals.inputTokens)}</TableCell>
                  <TableCell className="text-right">{fmtInt(data.totals.outputTokens)}</TableCell>
                  <TableCell className="text-right">{fmtUsd(data.totals.llmCostUsd)}</TableCell>
                  <TableCell className="text-right">{fmtEur(data.totals.billableEur)}</TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
